import LifestyleCard from "@components/ArticleCard/LifestyleCard";
import ReadMore from "@components/ArticleMeta/ReadMore";
import Container from "@components/Container/Container";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import CardImageV2 from "@particles/card/components/CardImageV2";
import {
  BasicCardProps,
  CardImage,
  CardVariations,
} from "@particles/card/utils/types";
import { getTruncateTitleLength } from "@routes/Section/utils/helpers";
import { cn, getDefaultImage, truncate } from "@util/helpers";

type LifestyleHeroProps = {
  articleProps: BasicCardProps;
};

const LifestyleHero = ({
  articleProps,
}: LifestyleHeroProps): React.ReactElement => {
  const { isSubscriber } = useUserInfo();
  const { isScreenMD, isScreenLG, isScreenXL } = useWindowSize();

  const truncateTitleLength = getTruncateTitleLength(isScreenLG, isScreenXL);
  const defaultThumbnail: CardImage = {
    alt: articleProps.title,
    width: 1570,
    height: 1046,
    src: getDefaultImage(articleProps.defaultImage?.directoryName || ""),
  };
  const variations: CardVariations = {
    title: {
      color: "text-white inline bg-black-100 px-[10px] decoration-clone",
      size: "text-6xl lg:text-11xl",
      extraClass: "font-pt-serif !leading-[1.2]",
      spanClassName: "inline",
    },
    kicker: {
      color: "text-white",
      extraClass:
        "break-words font-pt-sans text-4xs font-light leading-4 mb-3 bg-black-100 decoration-clone px-[10px] self-start",
    },
    section: {
      extraClass:
        "self-start bg-lifestyle-primary decoration-clone px-[10px] text-4xs mb-3",
    },
    content: {
      width: "w-full",
      blurb: {
        wrapperClassName: "mt-3",
        extraClass: cn(
          "font-public-sans text-xs font-normal",
          "text-xl bg-black-100 text-white hidden leading-6 mb-4 decoration-clone px-[10px] py-[2px] lg:inline"
        ),
      },
    },
    subscriberLabel: {
      extraClass: cn(
        "break-words font-pt-sans text-4xs font-light leading-4 mb-3 bg-black-100 decoration-clone px-[10px] self-start text-white"
      ),
    },
  };

  if (isScreenMD) {
    return (
      <div className="relative my-7" data-testid="lifestyle-hero-component">
        <CardImageV2
          src={articleProps.thumbnail?.src || defaultThumbnail.src}
          alt={articleProps.thumbnail?.alt || defaultThumbnail.alt}
          width={articleProps.thumbnail?.width || defaultThumbnail.width}
          height={articleProps.thumbnail?.height || defaultThumbnail.height}
          filters={{ w: 1920, q: 100, f: "webp" }}
        />

        <div className={cn("md:absolute md:inset-0 md:z-10")}>
          <Container>
            <div className="w-full md:mt-12 md:w-2/5">
              <Card
                {...articleProps}
                title={truncate(articleProps.title, truncateTitleLength)}
                thumbnail={!isScreenMD ? articleProps.thumbnail : undefined}
                defaultImage={
                  !isScreenMD ? articleProps.defaultImage : undefined
                }
                variations={variations}
                displayPaywall={isSubscriber && articleProps.displayPaywall}
              />

              <ReadMore to={articleProps.link} className="mt-3" isAnimated />
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative mb-7 mt-10 px-4"
      data-testid="lifestyle-hero-component"
    >
      <LifestyleCard cardProps={articleProps} />
    </div>
  );
};

export default LifestyleHero;
